import { useEffect, useRef, useState } from "react";
import { Heart, Flame, PartyPopper, Frown, Smile, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { tr } from "@/lib/translate";

export const REACTIONS = [
  { key: "love",  emoji: "❤️", icon: Heart,        color: "text-rose-500",   bg: "bg-rose-500/15" },
  { key: "fire",  emoji: "🔥", icon: Flame,        color: "text-orange-400", bg: "bg-orange-500/15" },
  { key: "wow",   emoji: "😮", icon: Sparkles,     color: "text-yellow-300", bg: "bg-yellow-500/15" },
  { key: "laugh", emoji: "😂", icon: Smile,        color: "text-amber-300",  bg: "bg-amber-500/15" },
  { key: "sad",   emoji: "😢", icon: Frown,        color: "text-sky-400",    bg: "bg-sky-500/15" },
  { key: "clap",  emoji: "👏", icon: PartyPopper,  color: "text-fuchsia-400", bg: "bg-fuchsia-500/15" },
];

const labelByKey = REACTIONS.reduce((acc, r) => ({ ...acc, [r.key]: r }), {});

/**
 * Facebook-style reaction picker.
 * - Click default = toggle "love"
 * - Long-press (mobile) / hover (desktop) opens the picker
 * - Picker click sets the reaction (re-click same = remove)
 */
export default function ReactionBar({ postId, summary, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState(summary?.my || null);
  const [breakdown, setBreakdown] = useState(summary?.breakdown || {});
  const longPressRef = useRef(null);
  const hoverRef = useRef(null);

  useEffect(() => {
    setMine(summary?.my || null);
    setBreakdown(summary?.breakdown || {});
  }, [summary]);

  const total = Object.values(breakdown).reduce((a, b) => a + b, 0);

  const apply = async (type) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${postId}/reactions`, { type });
      setMine(data.my);
      setBreakdown(data.breakdown);
      onChange && onChange(data);
    } catch {
      toast.error(tr("Could not update like"));
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const onTouchStart = () => {
    longPressRef.current = setTimeout(() => setOpen(true), 420);
  };
  const onTouchEnd = () => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  };
  const onMouseEnter = () => {
    if (hoverRef.current) clearTimeout(hoverRef.current);
    hoverRef.current = setTimeout(() => setOpen(true), 280);
  };
  const onMouseLeave = () => {
    if (hoverRef.current) clearTimeout(hoverRef.current);
    hoverRef.current = setTimeout(() => setOpen(false), 350);
  };

  const current = mine ? labelByKey[mine] : null;

  return (
    <div
      className="relative inline-block"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => apply(mine || "love")}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className={`flex items-center gap-2 px-3 py-2 rounded-full transition-colors ${current ? `${current.bg}` : "hover:bg-white/5"}`}
        data-testid={`${testid}-main`}
      >
        {current ? (
          <span className="text-xl leading-none" aria-label={current.key}>{current.emoji}</span>
        ) : (
          <Heart className="w-5 h-5 text-zinc-400" />
        )}
        <span className="text-sm text-zinc-300 tabular-nums">{total}</span>
      </button>

      {open && (
        <div
          className="absolute -top-14 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1 p-1.5 rounded-full bg-zinc-950/95 border border-white/10 backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.6)] anim-in"
          data-testid={`${testid}-picker`}
        >
          {REACTIONS.map((r, i) => (
            <button
              key={r.key}
              onClick={() => apply(r.key)}
              className={`relative w-9 h-9 grid place-items-center rounded-full transition-transform hover:scale-125 hover:-translate-y-1 ${mine === r.key ? "ring-2 ring-[#FF0050]" : ""}`}
              style={{ animationDelay: `${i * 0.04}s` }}
              data-testid={`${testid}-${r.key}`}
              aria-label={r.key}
            >
              <span className="text-xl">{r.emoji}</span>
              {breakdown[r.key] > 0 && (
                <span className="absolute -bottom-1 right-0 bg-zinc-900 text-white text-[10px] px-1 rounded-full border border-white/10">
                  {breakdown[r.key]}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
